import { Link } from "react-router-dom";
import { ThemeToggle } from "../theme.js";

/**
 * The public face of the product: what a visitor sees at "/" before they have a
 * session. Everything behind sign-in is tenant data, so this page is the only
 * one in the app that is not about one institution -- it is the only place the
 * product has to describe itself.
 *
 * The claims here are deliberately the ones the README can back. A lab platform
 * is bought by people who will test it, and "notices and records" is a promise
 * that survives that test where "prevents cheating" is not.
 */
export default function Landing() {
  return (
    <div className="landing">
      <header className="landing-nav">
        <div className="landing-nav-inner">
          <Link to="/" className="brand" style={{ color: "inherit" }}>
            <span className="brand-mark" aria-hidden="true">
              CB
            </span>
            CodeBench
          </Link>
          <div className="spacer" />
          {/* Hidden on a phone, where the sign-in button is the only thing that
              needs to survive. */}
          <nav className="landing-links">
            <a href="#features">Features</a>
            <a href="#exams">Exams</a>
            <a href="#sandbox">Sandbox</a>
          </nav>
          <ThemeToggle />
          <Link to="/login">
            <button className="primary">Sign in</button>
          </Link>
        </div>
      </header>

      <main>
        <section className="landing-hero">
          <div className="landing-hero-copy">
            <span className="eyebrow">For university computing labs</span>
            <h1>
              The C lab, <span className="accent-text">graded as it is written.</span>
            </h1>
            <p className="lede">
              Faculty publish lab sheets and exams. Students write, compile and iterate in
              the browser against real compiler diagnostics — then submit for grading
              against hidden test cases. No toolchain to install on forty machines.
            </p>
            <div className="landing-cta">
              <Link to="/login">
                <button className="primary lg">Sign in</button>
              </Link>
              <a href="#features">
                <button className="lg">See how it works</button>
              </a>
            </div>
            <p className="muted landing-note">
              Sign in with the account your institution issued you.
            </p>
          </div>

          {/* The product, not a picture of it: the frame below is built from the
              same components the workspace uses. */}
          <div className="landing-mock" aria-hidden="true">
            <div className="toolbar">
              <span className="badge">main.c</span>
              <div className="spacer" style={{ flex: 1 }} />
              <span className="muted" style={{ fontSize: 12 }}>
                2 s · 256 MB
              </span>
              <button className="primary" style={{ minHeight: 28, fontSize: 12.5 }}>
                Run
              </button>
            </div>
            <pre className="landing-code">
              <span className="tok-pre">#include</span> <span className="tok-str">&lt;stdio.h&gt;</span>
              {"\n\n"}
              <span className="tok-kw">int</span> <span className="tok-fn">main</span>(<span className="tok-kw">void</span>) {"{"}
              {"\n    "}
              <span className="tok-kw">int</span> w, h;
              {"\n    "}
              <span className="tok-fn">scanf</span>(<span className="tok-str">"%d %d"</span>, &amp;w, &amp;h);
              {"\n    "}
              <span className="tok-fn">printf</span>(<span className="tok-str">"%d %d\\n"</span>, w * h, <span className="tok-num">2</span> * (w + h));
              {"\n    "}
              <span className="tok-kw">return</span> <span className="tok-num">0</span>;
              {"\n"}
              {"}"}
            </pre>
            <div className="landing-mock-results">
              <div className="test-row">
                <span className="name">Square</span>
                <span className="muted" style={{ fontSize: 12 }}>3 ms</span>
                <span className="badge pass">passed</span>
              </div>
              <div className="test-row">
                <span className="name">Wide rectangle</span>
                <span className="muted" style={{ fontSize: 12 }}>2 ms</span>
                <span className="badge pass">passed</span>
              </div>
              <div className="test-row">
                <span className="name">Hidden · large values</span>
                <span className="muted" style={{ fontSize: 12 }}>4 ms</span>
                <span className="badge fail">failed</span>
              </div>
            </div>
          </div>
        </section>

        <section className="landing-section" id="features">
          <h2 className="landing-h2">Everything a lab runs on, in one place</h2>
          <p className="landing-sub">
            Courses, sheets, exams and marks — scoped to your institution and nobody
            else's.
          </p>

          <div className="landing-grid">
            <article className="landing-card">
              <h3>A real editor and a real compiler</h3>
              <p>
                Monaco in the browser, actual <code>gcc</code> diagnostics, and a Run
                button that takes the student's own stdin — before they ever submit.
              </p>
            </article>
            <article className="landing-card">
              <h3>Auto-graded against hidden tests</h3>
              <p>
                Each case carries a weight and a match mode: trimmed, byte-exact, or
                numeric within a tolerance. The score is the fraction of passed weight.
              </p>
            </article>
            <article className="landing-card">
              <h3>Marks a teacher still owns</h3>
              <p>
                Any auto-mark can be overridden. Automatic and manual scores are stored
                separately, so both stay visible on the record.
              </p>
            </article>
            <article className="landing-card">
              <h3>A question library</h3>
              <p>
                Reusable programming and multiple-choice questions, tagged by topic and
                difficulty — plus a stock bank of 50 C questions to start from.
              </p>
            </article>
            <article className="landing-card">
              <h3>A scratch playground</h3>
              <p>
                The same compiler with no question attached, for demonstrating a loop in
                class or trying a language feature. Nothing stored, nothing marked.
              </p>
            </article>
            <article className="landing-card">
              <h3>Roster, batches and terms</h3>
              <p>
                Enrol by batch, run a course across semesters, and follow a student's
                marks across their degree rather than one term at a time.
              </p>
            </article>
          </div>
        </section>

        <section className="landing-section landing-split" id="exams">
          <div>
            <h2 className="landing-h2">Exams that are honest about proctoring</h2>
            <p className="landing-sub">
              An exam is time-boxed, sat once, and drawn from the same question pool as the
              course. Tab switches, focus loss and leaving fullscreen are reported to the
              server — which owns the count, because the student owns the page. Past your
              threshold it warns, auto-submits, or locks the attempt.
            </p>
            <div className="landing-callout">
              <strong>What a browser cannot do.</strong> It cannot stop a second window, a
              phone, or Cmd-Tab — the operating system owns those, and any tool claiming
              otherwise is misleading whoever trusts it. Invigilation still needs a person
              in the room. What this gives you is a record.
            </div>
          </div>
          <ul className="landing-list">
            <li>
              <strong>Mixed papers.</strong> Programming questions and multiple choice in
              one exam.
            </li>
            <li>
              <strong>Every violation is an audit entry</strong> naming the kind, the exam
              and the time.
            </li>
            <li>
              <strong>Optional screen capture</strong> at the moment a student leaves the
              window — never while they are working in it, never continuously, and off
              unless the exam asks for it.
            </li>
            <li>
              <strong>Expired attempts submit themselves,</strong> so a closed laptop is
              still a graded paper.
            </li>
          </ul>
        </section>

        <section className="landing-section" id="sandbox">
          <h2 className="landing-h2">Student code runs contained</h2>
          <p className="landing-sub">
            Every submission is a subprocess under POSIX resource limits, spawned with an
            empty environment so server config never reaches student code.
          </p>
          <div className="landing-limits">
            <div className="landing-limit">
              <span className="k">RLIMIT_CPU</span>
              <span className="v">busy infinite loops</span>
            </div>
            <div className="landing-limit">
              <span className="k">RLIMIT_AS</span>
              <span className="v">runaway malloc</span>
            </div>
            <div className="landing-limit">
              <span className="k">RLIMIT_NPROC</span>
              <span className="v">fork bombs</span>
            </div>
            <div className="landing-limit">
              <span className="k">RLIMIT_FSIZE</span>
              <span className="v">filling the disk</span>
            </div>
            <div className="landing-limit">
              <span className="k">RLIMIT_NOFILE</span>
              <span className="v">descriptor exhaustion</span>
            </div>
            <div className="landing-limit">
              <span className="k">wall-clock kill</span>
              <span className="v">everything else</span>
            </div>
          </div>
          <p className="muted landing-note">
            The suite that proves it ships with the code: hostile programs — fork bomb, 12
            GB allocation, output flood, disk filler — run on every machine you deploy to.
          </p>
        </section>

        <section className="landing-final">
          <h2 className="landing-h2">Ready when your lab is</h2>
          <p className="landing-sub">
            Runs on a machine in the department or on a server you rent. Accounts are
            issued by your institution.
          </p>
          <div className="landing-cta">
            <Link to="/login">
              <button className="primary lg">Sign in to CodeBench</button>
            </Link>
          </div>
        </section>
      </main>

      <footer className="landing-footer">
        <span className="brand-mark" aria-hidden="true">
          CB
        </span>
        <span className="muted">CodeBench — lab assignments, exams and marking.</span>
        <div className="spacer" style={{ flex: 1 }} />
        <Link to="/login">Sign in</Link>
      </footer>
    </div>
  );
}
